<template>
    <input ref="input" :id="_id" v-model="input" :name="name" :type="type" :required="_required" :aria-required="_required ? 'true' : 'false'"
        :class="[sizeClass, {invalid: isInvalid}]" v-bind="ariaValidationAttributes" v-on="events" v-focus="autofocus">
</template>

<script lang="ts">
    import {Component, Prop, Watch} from 'vue-facing-decorator';
    import AbstractFormInput from '../../mixins/AbstractFormInput';

    @Component({
        name: 'FormInput',
        emits: ['update:modelValue'],
    })
    export class FormInput extends AbstractFormInput
    {
        readonly $refs: { input: HTMLInputElement };

        @Prop({
            type: String,
            default: 'text',
            required: false,
        }) type: string;

        @Prop({
            type: [Number, String, Boolean],
        }) modelValue: InputValueType;

        @Prop({
            type: Object,
            default()
            {
                return {};
            },
        }) context: object;

        @Prop({
            type: String,
            default: 'long',
            required: false,
            validator: function(value)
            {
                return ['none', 'long', 'medium', 'small'].includes(value);
            },
        }) size: string;

        @Prop({
            type: Object,
            default()
            {
                return {};
            },
        }) events: object;

        @Prop({
            type: Boolean,
            default: false,
        }) autofocus: boolean;

        input: InputValueType = '';

        timeout: number = 0;

        internalInput: boolean = false;

        externalInput: boolean = false;

        get sizeClass()
        {
            return 'none' !== this.size && this.size || '';
        }

        @Watch('input')
        watchInput(val: InputValueType)
        {
            this.internalInput = !this.externalInput;

            this.$emit('update:modelValue', val);
        }

        @Watch('modelValue')
        watchValue(newValue: InputValueType, oldValue: InputValueType)
        {
            this.externalInput = !this.internalInput;

            // 'fake' debounce
            if (this.timeout)
            {
                clearTimeout(this.timeout);
            }

            this.timeout = window.setTimeout(() =>
            {
                this.input = newValue;

                if (newValue !== oldValue)
                {
                    this.$nextTick(this.updateValue);
                }
            }, 200);
        }

        created()
        {
            this.input = this.modelValue || this.modelValue === 0 ? this.modelValue : '';
        }

        updateValue()
        {
            let input           = this.input;
            const externalInput = this.externalInput;

            this.internalInput = false;
            this.externalInput = false;

            if (this.$refs.input)
            {
                input = this.$refs.input.value;
            }

            if (!input && externalInput)
            {
                return;
            }

            return this.validateValue(input, this.context);
        }

        focus()
        {
            (this.$el as HTMLInputElement)?.focus();
        }
    }

    export default FormInput;
</script>